import clsx from 'clsx';
import {
  Children,
  forwardRef,
  isValidElement,
  type ForwardedRef,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import { listCSS, listItemCSS } from './List.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { Text } from '../text/Text.js';

const COMPONENT_NAME = 'List';

/**
 * The props for the Link component.
 * @public
 */
export interface ListProps<TOrdered extends boolean = false>
  extends WithChildren,
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * Whether the List is ordered or not. This changes whether numbers or bullets are used.
   * @defaultValue false
   */
  ordered?: TOrdered;
  /**
   * Sets the text style for the List.
   * @defaultValue 'base'
   */
  textStyle?: 'base' | 'base-emphasized' | 'small' | 'small-emphasized';
  /**
   * Sets the text style for the List.
   * @defaultValue 'text'
   */
  fontStyle?: 'text' | 'code';
  /**
   * Sets the starting number of the first item in an ordered list.
   * The value is always treated as an integer (floor).
   * @defaultValue 1
   */
  start?: number;
}

/** Maps a list of (potentially) nested list items to HTML list structure. */
function interleaveListItems(listItems: ReactNode) {
  const items: ReactNode[][] = [];

  Children.forEach(listItems, (listItem) => {
    if (isValidElement(listItem)) {
      if (listItem.type === List) {
        if (items.length === 0) {
          items.push([listItem]);
        } else {
          items[items.length - 1].push(listItem);
        }
      } else {
        items.push([listItem]);
      }
    }
  });

  return items.map((values, index) => {
    const itemKey = `list-item-${getItemKey(values, index)}`;
    return (
      <li key={itemKey} className={listItemCSS}>
        {values}
      </li>
    );
  });
}

/**
 * Generates a key for a list item. We try to avoid
 * generating [Object object] by looking down levels
 * recursively. If there is an existing key defined in
 * a descendant uses that as part of the key otherwise
 * if we get a string value, use that. Otherwise uses
 * the index if nothing was found.
 * @param values - nodes inside the list item
 * @param index - current zero based index
 * @returns a key generated for the item
 */
function getItemKey(values: ReactNode, index: number): string | number {
  const isArray = Array.isArray(values);
  const value = isArray ? values?.[0] : values;
  if (isValidElement<PropsWithChildren>(value)) {
    if (value.key) {
      return value.key;
    } else if (value.props && value.props.children) {
      return getItemKey(value.props.children, index);
    }
    return value as unknown as string | number;
  }

  if (value === undefined) {
    return index;
  }

  return value as unknown as string | number;
}

/**
 * The `List` component groups a set of related content in a list and can be arbitrarily nested.
 * List items are preceded by either a consecutive number (for ordered lists) or a bullet point
 * (for unordered lists).
 * @public
 */
export const List = /* @__PURE__ */ forwardRef(
  <TOrdered extends boolean = false>(
    props: ListProps<TOrdered>,
    ref: ForwardedRef<
      TOrdered extends true ? HTMLOListElement : HTMLUListElement
    >,
  ) => {
    const {
      children,
      ordered = undefined,
      textStyle,
      fontStyle,
      start = 1,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    return (
      <Text
        ref={ref}
        as={ordered ? 'ol' : 'ul'}
        {...(ordered && {
          start,
        })}
        textStyle={textStyle}
        fontStyle={fontStyle}
        className={clsx(listCSS, consumerClassName)}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        {interleaveListItems(children)}
      </Text>
    );
  },
);

(List as typeof List & { displayName: string }).displayName = COMPONENT_NAME;
